import React, { useState } from "react";
import { AppBar, Toolbar, Button, Box, Menu, MenuItem, IconButton, Drawer, List, ListItem, ListItemText } from "@mui/material";
import { styled } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../Assets/logo.svg";
import { useNavigate, useLocation } from "react-router-dom";

const NavBarContainer = styled(AppBar)({
  backgroundColor: "rgba(0, 0, 50, 0.7)", // Dark blue transparent
  backdropFilter: "blur(10px)", // Glass effect
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
});

const LogoImg = styled("img")({
  width: 50,
  height: "auto",
  cursor: "pointer",
});

const NavLinks = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "20px",
  [theme.breakpoints.down("md")]: {
    display: "none", // Hide links on small screens
  },
}));

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [servicesMenu, setServicesMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isInServices = location.pathname.startsWith("/services");

  // Open Dropdown if inside /services
  const handleServicesClick = (event) => {
    if (isInServices) {
      setServicesMenu(event.currentTarget);
    } else {
      navigate("/services");
    }
  };

  const handleServicesClose = () => setServicesMenu(null);

  // Toggle mobile drawer
  const toggleDrawer = (state) => () => setMobileOpen(state);

  return (
    <NavBarContainer position="static">
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* Logo */}
        <Box onClick={() => navigate("/")} sx={{ cursor: "pointer" }}>
          <LogoImg src={Logo} alt="Logo" />
        </Box>

        {/* Desktop Navigation Links */}
        <NavLinks>
          <Button sx={navButtonStyle} onClick={() => navigate("/")}>Home</Button>
          <Button sx={navButtonStyle} onClick={() => navigate("/about")}>About</Button>
          
          {/* Services Button - Navigates or Opens Dropdown */}
          <Button sx={navButtonStyle} onClick={handleServicesClick}>
            Services
          </Button>

          {/* Show dropdown only if inside /services */}
          {isInServices && (
            <Menu
              anchorEl={servicesMenu}
              open={Boolean(servicesMenu)}
              onClose={handleServicesClose}
              sx={{
                mt: 1,
                "& .MuiPaper-root": {
                  backgroundColor: "rgba(0, 0, 50, 0.9)", // Dark blue transparent dropdown
                  backdropFilter: "blur(10px)",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.5)",
                },
              }}
            >
              <MenuItem sx={menuItemStyle} onClick={() => { handleServicesClose(); navigate("/services/web-dev"); }}>Web Development</MenuItem>
              <MenuItem sx={menuItemStyle} onClick={() => { handleServicesClose(); navigate("/services/servicenow"); }}>ServiceNow</MenuItem>
              <MenuItem sx={menuItemStyle} onClick={() => { handleServicesClose(); navigate("/services/staffing"); }}>Staffing</MenuItem>
              <MenuItem sx={menuItemStyle} onClick={() => { handleServicesClose(); navigate("/services/consultancy"); }}>Consultancy</MenuItem>
            </Menu>
          )}

          <Button sx={navButtonStyle} onClick={() => navigate("/career")}>Careers</Button>
          <Button sx={navButtonStyle} onClick={() => navigate("/contact")}>Contact Us</Button>
        </NavLinks>

        {/* Hamburger Icon for Mobile */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={toggleDrawer(true)}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Drawer (Hamburger Menu) */}
      <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer(false)}>
        <List sx={{ width: 250, backgroundColor: "rgba(0, 0, 50, 0.9)", height: "100%", color: "white" }}>
          <ListItem button onClick={() => { navigate("/"); toggleDrawer(false)(); }}>
            <ListItemText primary="Home" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button onClick={() => { navigate("/about"); toggleDrawer(false)(); }}>
            <ListItemText primary="About" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button onClick={() => { navigate("/services"); toggleDrawer(false)(); }}>
            <ListItemText primary="Services" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button sx={{ pl: 4 }} onClick={() => { navigate("/services/web-dev"); toggleDrawer(false)(); }}>
            <ListItemText primary="Web Development" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button sx={{ pl: 4 }} onClick={() => { navigate("/services/servicenow"); toggleDrawer(false)(); }}>
            <ListItemText primary="ServiceNow" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button sx={{ pl: 4 }} onClick={() => { navigate("/services/staffing"); toggleDrawer(false)(); }}>
            <ListItemText primary="Staffing" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button sx={{ pl: 4 }} onClick={() => { navigate("/services/consultancy"); toggleDrawer(false)(); }}>
            <ListItemText primary="Consultancy" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button onClick={() => { navigate("/career"); toggleDrawer(false)(); }}>
            <ListItemText primary="Careers" sx={drawerItemStyle} />
          </ListItem>
          <ListItem button onClick={() => { navigate("/contact"); toggleDrawer(false)(); }}>
            <ListItemText primary="Contact Us" sx={drawerItemStyle} />
          </ListItem>
        </List>
      </Drawer>
    </NavBarContainer>
  );
};

// Reusable Styles
const navButtonStyle = {
  color: "white",
  borderRadius: "4px",
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "'Inter', sans-serif",
  textTransform: "none",
  transition: "color 0.3s ease-in-out",
  "&:hover": { color: "#ffcc00" }, // Gold hover effect
};

const menuItemStyle = {
  color: "white",
  fontSize: "14px",
  fontWeight: "500",
  fontFamily: "'Inter', sans-serif",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)", // Subtle hover effect
    color: "#ffcc00", // Gold hover effect
  },
};

const drawerItemStyle = {
  color: "white",
  fontWeight: "500",
  "&:hover": {
    color: "#ffcc00", // Gold hover effect
  },
};

export default Navbar;
